import React, { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { ReturnComponentType } from 'common';
import { useAppSelector, useTimecodes, useTypedDispatch } from 'hooks';
import {
  TimeCodeType,
  editTimeCode,
  fetchTimeCodes,
  selectCertainInspection,
  selectFiles,
} from 'state';
import './TimecodeTrimBar.scss';
import {
  convertNumberToTime,
  convertTimeToNumber,
  getFileAtTime,
  shortenTimeString,
} from 'utils';

type TimecodeTrimBarType = {
  progress: number;
  handleManualInspectionProgress: (manualChange: number) => void;
  timecodes: TimeCodeType[];
};

export const TimecodeTrimBar = ({
  progress,
  handleManualInspectionProgress,
  timecodes,
}: TimecodeTrimBarType): ReturnComponentType => {
  const SECOND_WIDTH = 48;
  const { inspectionDuration: duration } = useTimecodes();
  const inspection = useAppSelector(selectCertainInspection);
  const dispatch = useTypedDispatch();
  const files = useAppSelector(selectFiles);
  const [draggedHandle, setDraggedHandle] = useState<
    { end: string; id: string } | undefined
  >();
  const [dropPanelWidth, setDropPanelWidth] = useState<number>();
  const [includedSeconds, setIncludedSeconds] = useState<number[]>([]);
  const [showStationaryProgress, setShowStationaryProgress] = useState(false);
  const dropPanelRef = useRef<HTMLDivElement | null>(null);
  const scrollableRef = useRef<HTMLDivElement | null>(null);

  const setProgress = (e: React.MouseEvent) => {
    const newProgress = getSecondsFromPosition(e.clientX, false);

    if (!newProgress) return;

    handleManualInspectionProgress(newProgress);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const getSecondsFromPosition = (
    x: number,
    shouldRound: boolean = true,
  ): number | undefined => {
    if (!dropPanelRef.current || !scrollableRef.current) return;

    const dropX =
      x +
      scrollableRef.current.scrollLeft -
      scrollableRef.current.getBoundingClientRect().left;

    return shouldRound ? Math.round(dropX / SECOND_WIDTH) : dropX / SECOND_WIDTH;
  };

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!dropPanelRef.current || !draggedHandle) return;

    const inspectionSeconds = getSecondsFromPosition(e.clientX, true);

    if (!inspectionSeconds) return;

    const fileSeconds = Math.ceil(getFileAtTime(files, inspectionSeconds).timeWithinFile);

    const draggedTimecode = timecodes.find(tc => {
      return tc.id === draggedHandle.id;
    });

    if (!draggedTimecode) return;

    // once we have the time stamp, set the beginning or end of that timecode
    const newTimecode = {
      timecodeStart: draggedTimecode.timecodeStart,
      timecodeEnd: draggedTimecode.timecodeEnd,
      tagId: draggedTimecode.tagId,
      id: draggedTimecode.id,
    };

    if (draggedHandle.end === 'start') {
      // prevent making it >= end
      const newNumber = Math.min(
        fileSeconds || 0,
        convertTimeToNumber(draggedTimecode.timecodeEnd) - 1,
      );

      newTimecode.timecodeStart = convertNumberToTime(newNumber);
    }

    if (draggedHandle.end === 'end') {
      // prevent making it >= start
      const newNumber = Math.max(
        fileSeconds || 0,
        convertTimeToNumber(draggedTimecode.timecodeStart) + 1,
      );

      newTimecode.timecodeEnd = convertNumberToTime(newNumber);
    }

    const status = await dispatch(editTimeCode(newTimecode));

    if (status?.status && inspection.id) {
      dispatch(fetchTimeCodes(inspection.id));
    }
  };

  const onDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    // eslint-disable-next-line no-param-reassign
    e.dataTransfer.effectAllowed = 'move';

    const split = (e.target as HTMLDivElement).id.split('_');

    const end = split[2] || undefined;

    const id = split[3] || undefined;

    if (end && id) {
      setDraggedHandle({ end, id });
    }
  };

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      if (!dropPanelRef.current) return;

      setDropPanelWidth(dropPanelRef.current.getBoundingClientRect().width);
    });

    if (dropPanelRef.current) {
      observer.observe(dropPanelRef.current);

      return () => observer.disconnect();
    }
  }, []);

  // scroll to progress (i.e. generally keep it in the middle)
  useEffect(() => {
    if (!scrollableRef.current || !dropPanelWidth) return;

    const left =
      progress * SECOND_WIDTH - scrollableRef.current.getBoundingClientRect().width / 2;

    scrollableRef.current.scrollTo({
      top: 0,
      left,
      behavior: 'smooth',
    });

    // if the progress should be in the middle, display it stationarily. otherwise, allow it to move
    if (progress * SECOND_WIDTH < dropPanelWidth / 2) {
      setShowStationaryProgress(false);
    } else if (duration * SECOND_WIDTH - progress * SECOND_WIDTH < dropPanelWidth / 2) {
      setShowStationaryProgress(false);
    } else {
      setShowStationaryProgress(true);
    }
  }, [dropPanelWidth, duration, progress, scrollableRef]);

  useEffect(() => {
    const seconds = Array.from(new Array(Math.floor(duration)), (x, i) => i);

    setIncludedSeconds(seconds);
  }, [duration]);

  return (
    <section className="VideoPlayer_TrimBar" ref={dropPanelRef}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="VideoPlayer_TrimBar_Container"
        onDragOver={onDragOver}
        onDrop={onDrop}
        onClick={setProgress}
        ref={scrollableRef}
      >
        {includedSeconds?.map(second => {
          return (
            <div key={`second-${second}`} className="VideoPlayer_TrimBar_Second">
              {shortenTimeString(duration, convertNumberToTime(second))}
            </div>
          );
        })}
        {timecodes?.map((timecode, i) => {
          const {
            inspectionTimecodeStart: timecodeStart,
            inspectionTimecodeEnd: timecodeEnd,
          } = timecode;

          if (!timecodeStart || !timecodeEnd) return;

          const timecodeDuration = timecodeEnd - timecodeStart;

          const isImmediatelyAfterATimecode =
            timecodeStart === timecodes[i - 1]?.inspectionTimecodeEnd;
          const isImmediatelyBeforeATimecode =
            timecodeEnd === timecodes[i + 1]?.inspectionTimecodeStart;

          const timecodeWidth =
            timecodeDuration * SECOND_WIDTH -
            (isImmediatelyAfterATimecode ? 8 : 0) -
            (isImmediatelyBeforeATimecode ? 8 : 0) -
            4;

          return (
            <React.Fragment key={`timecode-${timecode.id}`}>
              <div
                className="VideoPlayer_TrimBar_Timecode"
                style={{
                  left: isImmediatelyAfterATimecode
                    ? `${timecodeStart * SECOND_WIDTH + 8}px`
                    : `${timecodeStart * SECOND_WIDTH}px`,
                  width: timecodeWidth,
                }}
                title={timecode.propertyAddress}
              >
                {timecode.propertyAddress}
              </div>
              <div
                id={`timecode_handle_start_${timecode.id}`}
                className={cn(
                  'VideoPlayer_TrimBar_HandleStart',
                  isImmediatelyAfterATimecode && 'Butting',
                )}
                draggable
                onDragStart={onDragStart}
                style={{ left: `${timecodeStart * SECOND_WIDTH}px` }}
              >
                <div>|</div>
              </div>
              <div
                id={`timecode_handle_end_${timecode.id}`}
                className={cn(
                  'VideoPlayer_TrimBar_HandleEnd',
                  isImmediatelyBeforeATimecode && 'Butting',
                )}
                draggable
                onDragStart={onDragStart}
                style={{ left: `${timecodeEnd * SECOND_WIDTH + 8}px` }}
              >
                <div>|</div>
              </div>
            </React.Fragment>
          );
        })}
        {!showStationaryProgress && (
          <div
            className="VideoPlayer_TrimBar_Progress"
            style={{
              left: `${progress * SECOND_WIDTH}px`,
            }}
          />
        )}
      </div>
      {showStationaryProgress && (
        <div className="VideoPlayer_TrimBar_ProgressStationary" />
      )}
    </section>
  );
};
